import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import dayjs, { type Dayjs } from 'dayjs';
import type { TempRange } from '../types';

interface CalendarProps {
  month: Dayjs;
  onMonthChange: (month: Dayjs) => void;
  tempRange: TempRange;
  hoveredDate: Dayjs | null;
  onSelect: (date: Dayjs) => void;
  onHover: (date: Dayjs | null) => void;
}

const Calendar: React.FC<CalendarProps> = ({
  month,
  onMonthChange,
  tempRange,
  hoveredDate,
  onSelect,
  onHover,
}) => {
  const monthStart = month.startOf('month');
  const monthEnd = month.endOf('month');
  const startDate = monthStart.startOf('week');
  const endDate = monthEnd.endOf('week');

  const days: Dayjs[] = [];
  let day = startDate;
  while (day.isBefore(endDate) || day.isSame(endDate)) {
    days.push(day);
    day = day.add(1, 'day');
  }

  const weeks: Dayjs[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => onMonthChange(month.subtract(1, 'month'))}
          className="p-1 hover:bg-gray-100 rounded"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="font-medium">{month.format('MMMM YYYY')}</div>
        <button
          onClick={() => onMonthChange(month.add(1, 'month'))}
          className="p-1 hover:bg-gray-100 rounded"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-xs text-gray-500 mb-2">
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
          <div key={d} className="text-center font-medium">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {weeks.map((week) =>
          week.map((d) => {
            const isToday = d.isSame(dayjs(), 'day');
            const isCurrentMonth = d.isSame(month, 'month');

            const isSelected =
              (tempRange.from && d.isSame(tempRange.from, 'day')) ||
              (tempRange.to && d.isSame(tempRange.to, 'day')) ||
              !!(tempRange.from && tempRange.to && d.isBetween(tempRange.from, tempRange.to, 'day'));

            let isHoveredInRange = false;
            if (tempRange.from && !tempRange.to && hoveredDate) {
              const start = tempRange.from.isBefore(hoveredDate) ? tempRange.from : hoveredDate;
              const end = tempRange.from.isBefore(hoveredDate) ? hoveredDate : tempRange.from;
              isHoveredInRange = d.isBetween(start, end, 'day', '[]') || d.isSame(start, 'day') || d.isSame(end, 'day');
            }

            const isInRange =
              !isSelected && tempRange.from && tempRange.to && d.isBetween(tempRange.from, tempRange.to, 'day');

            return (
              <button
                key={d.format()}
                onClick={() => onSelect(d)}
                onMouseEnter={() => onHover(d)}
                onMouseLeave={() => onHover(null)}
                className={`h-8 rounded text-sm transition-colors ${
                  isSelected
                    ? 'bg-blue-500 text-white'
                    : isHoveredInRange
                    ? 'bg-blue-100'
                    : isInRange
                    ? 'bg-blue-100'
                    : isToday && !isSelected
                    ? 'bg-gray-900 text-white'
                    : isCurrentMonth
                    ? 'text-gray-900'
                    : 'text-gray-400'
                } hover:bg-gray-100`}
              >
                {d.format('D')}
              </button>
            );
          }),
        )}
      </div>
    </div>
  );
};

export default Calendar;
